import { TimezoneModel } from "../models/data/timezone.model";
import { environment } from "../../../environments/environment";
import { SystemMessages } from "../enums/base.enums";

export class GlobalConstants {
    public static ROOT_IMAGE_PATH = (): string => "public\\images";
    public static ASCENDING_ABBREVIATION = (): string => "Asc";
    public static DESCENDING_ABBREVIATION = (): string => "Desc";
    public static DEFAULT_HEX_COLOR = (): string => "#00aeef";

    // The primary/accent color is tuned independently per theme: the darker blue reads as vivid and
    // legible on the light background, but washes out on dark, so dark mode keeps the brighter blue
    // that was already chosen to pop there.
    public static DEFAULT_PRIMARY_COLOR_LIGHT = (): string => "#00649d";
    public static DEFAULT_PRIMARY_COLOR_DARK = (): string => "#00aeef";
}

export class ApiBaseUrls {
    public static AUTHENTICATION = (): string => environment.apiAuthenticationPath;
    public static CORE = (): string => environment.apiCorePath;
    public static MESSAGING = (): string => environment.apiMessagingPath;
}

export class ApiControllers {
    public static USERS = (): string => "users";
    public static ORGANIZATIONS = (): string => "organizations";
    public static MEMBERS = (): string => "members";
    public static PROPERTIES = (): string => "properties";
    public static LOCATIONS = (): string => "locations";
    public static ADDRESSES = (): string => "addresses";
    public static AUTHORIZATION = (): string => "authorization";
    public static MESSAGE_SUPPRESSION = (): string => "message-suppression";
    public static MESSAGES = (): string => "messages";
    public static FOLDERS = (): string => "folders";
    public static DOCUMENTS = (): string => "documents";
    public static CHARGES = (): string => "charges";
    public static PAYMENTS = (): string => "payments";
    public static FINANCIALS = (): string => "financials";
    public static CHART_OF_ACCOUNTS = (): string => "chart-of-accounts";
    public static BANK_CONNECTIONS = (): string => "bank-connections";
    public static BANK_TRANSACTIONS = (): string => "bank-transactions";
    public static SUPPORT = (): string => "support";
    public static FEATURE_FLAGS = (): string => "feature-flags";
    public static IMPORTS = (): string => "imports";
}

export class ApiSettings {
    public static DEFAULT_TAKE = (): number => 20;

    public static DEFAULT_CACHE_TIMEOUT_MS = (): number => 60000;

    public static DEFAULT_ORG_TIMEZONE_FALLBACK = (): TimezoneModel => <TimezoneModel>{
        id: "Eastern Standard Time",
        displayName: "(UTC-05:00) Eastern Time (US & Canada)",
        standardName: "Eastern Standard Time",
        daylightName: "Eastern Daylight Time",
        baseUtcOffset: "-05:00:00",
        isDaylightSavingsTime: false
    };
}

export class PaginationConstants {
    public static PAGINATION_DEFAULT_PAGE_SIZE = (): number => 10;
    public static PAGINATION_MAX_PAGE_SIZE = (): number => 100;
    public static LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS = (): number => 1000;
    public static SEARCH_DEBOUNCE_TIME_MILLISECONDS = (): number => 300;
    public static DEFAULT_SCROLL_TO_ELEMENT_NAME = (): string => "#jumpToElement";
    public static DEFAULT_SCROLL_TO_ELEMENT_MOBILE_NAME = (): string => "#jumpToElementMobile";
}

export class ErrorMessages {
    public static DEFAULT_ERROR_MESSAGE = (): string => "We encountered an error trying to process your request.";
    public static DEFAULT_FAILURE_MESSAGE = (): string => "We failed to process your request.";
}

export class FormValidationRegEx {
    public static ZIP_POSTAL_US_PATTERN = () => /^(\d{5}(\d{4})?)$/;
    public static ZIP_POSTAL_CA_PATTERN = () => /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
    // Shape only, for the seven international countries: the API caps the field at 20 and the
    // verification provider is the authority on whether a code actually exists.
    public static ZIP_POSTAL_INTERNATIONAL_PATTERN = () => /^[A-Za-z0-9][A-Za-z0-9 -]{0,19}$/;
}

export class AuditLogParsingRegEx {
    public static FORMAT_DETAIL_PATTERN = () => /(\{[^}]+\}|\[[^\]]+\])/;    
    public static REPLACE_WITH_ITALICS_PATTERN = () => /\|([^|]+)\|/g;
}

export class LocalStorageKeyConstants {
    // Per bank connection: the API never stores the statement balance, so remembering it here is what
    // stops a reload making the user re-read their statement. Per-browser by nature, never authoritative.
    public static BANK_STATEMENT = (bankConnectionId: number): string =>
        `membershine_bank_statement_${bankConnectionId}`;
    public static USERS_FILTER = (): string => 'membershine_users_filter';
    public static USERS_ORDERBY = (): string => 'membershine_users_orderby';
    public static ORGANIZATIONS_FILTER = (): string => 'membershine_organizations_filter';
    public static ORGANIZATIONS_ORDERBY = (): string => 'membershine_organizations_orderby';
    public static MEMBERS_FILTER = (): string => 'membershine_members_filter';
    public static MEMBERS_ORDERBY = (): string => 'membershine_members_orderby';
    public static PROPERTIES_FILTER = (): string => 'membershine_properties_filter';
    public static PROPERTIES_ORDERBY = (): string => 'membershine_properties_orderby';
    public static PROPERTY_CHARGE_SCHEDULES_FILTER = (): string => 'membershine_property_charge_schedules_filter';
    public static PROPERTY_CHARGE_SCHEDULES_ORDERBY = (): string => 'membershine_property_charge_schedules_orderby';
    public static BUDGETS_FILTER = (): string => 'membershine_budgets_filter';
    public static BUDGETS_ORDERBY = (): string => 'membershine_budgets_orderby';
    public static PAYMENT_HISTORY_FILTER = (): string => 'membershine_payment_history_filter';
    public static PAYMENT_HISTORY_ORDERBY = (): string => 'membershine_payment_history_orderby';
    public static EXPENSES_FILTER = (): string => 'membershine_expenses_filter';
    public static EXPENSES_ORDERBY = (): string => 'membershine_expenses_orderby';
    public static DOCUMENTS_ORDERBY = (): string => 'membershine_documents_orderby';
    // List view's independent per-table sort overrides (DEV-2572) — absent unless the user has actually
    // diverged that table's sort from the toolbar's DOCUMENTS_ORDERBY baseline via its own header.
    public static DOCUMENTS_FOLDER_ORDERBY = (): string => 'membershine_documents_folder_orderby';
    public static DOCUMENTS_FILE_ORDERBY = (): string => 'membershine_documents_file_orderby';
    // The user's Document Center List/Grid layout preference (DEV-2559). Per-user (not per-org), so it
    // follows the user across every organization they visit — mirrors UserSessionService's
    // activeOrganizationLocalStorageKey, unlike the flat/global keys above (shared per-browser).
    public static DOCUMENTS_VIEW_LAYOUT = (userId: number): string => `membershine_documents_view_layout_user_${userId}`;
    public static SENT_EMAILS_FILTER = (): string => 'membershine_sent_emails_filter';
    public static SENT_EMAILS_ORDERBY = (): string => 'membershine_sent_emails_orderby';
    // Row heights measured from each list's own rendered rows, so a list's first page can be sized to
    // fill the viewport (DEV-2535). Per-browser rather than per-user — it describes the rendered CSS,
    // which is the same whoever is signed in.
    public static LIST_ROW_HEIGHTS = (): string => 'membershine_list_row_heights';

    // Dismissed informational notes (DEV-2826). Scope is 'global' for staff personas (one dismissal
    // follows them across every org) and 'org_{orgId}' for org-scoped members (dismissal is per
    // member, per organization). Prefix sits outside clearLocalStorage's wipe patterns so dismissals
    // survive sign-out, which the ticket's AC requires.
    public static DISMISSED_NOTE = (userId: number, scope: string, noteKey: string): string =>
        `membershine_dismissed_note_user_${userId}_${scope}_${noteKey}`;
}

// Router commands for the system/:messageId page. Guards redirect here rather than spelling the
// path, which drifted into four different forms across the guards before this existed.
export class SystemPageRoutes {
    public static for = (message: SystemMessages): string[] => ["system", message];

    public static FORBIDDEN = (): string[] => SystemPageRoutes.for(SystemMessages.Forbidden);
    public static NOT_FOUND = (): string[] => SystemPageRoutes.for(SystemMessages.PageNotFound);
    public static AUTH_ERROR = (): string[] => SystemPageRoutes.for(SystemMessages.AuthError);
    public static MAINTENANCE = (): string[] => SystemPageRoutes.for(SystemMessages.Maintenance);
    public static ACCOUNT_DEACTIVATED = (): string[] => SystemPageRoutes.for(SystemMessages.AccountDeactivated);
}

export class MaintenanceConstants {
    // The API answers with this pair while system.maintenance is on. The code is what identifies it -
    // infrastructure 503s from App Service or Front Door carry no code.
    public static MAINTENANCE_STATUS_CODE = (): number => 503;
    public static MAINTENANCE_ERROR_CODE = (): string => "maintenance";

    // The dashboard's own cadence; there is no Retry-After to honour.
    public static POLL_INTERVAL_MS = (): number => 60000;

    // A probe that fails this way says nothing about maintenance: the API is briefly unreachable, as
    // it is while a deploy restarts it. 503 is included because one without the maintenance code came
    // from the infrastructure, not from us.
    public static INFRASTRUCTURE_FAILURE_STATUSES = (): number[] => [502, 503, 504];

    // Any route the maintenance check does not exempt answers the landing probe; the exempt ones
    // keep returning 200 and would report the site as up. Reference data is the cheapest of them,
    // takes no id, and is harmless to call unauthenticated.
    public static PROBE_PATH = (): string => "timezones";

    // Escape hatch for QA: /system/maintenance?signOut=true signs the current user out. Sign-in
    // re-authenticates anyone who still has a session, so without this someone signed in as a member
    // cannot switch to their System Administrator account while maintenance is on. Deliberately
    // unadvertised - it is a debugging aid, not a feature.
    public static SIGN_OUT_QUERY_PARAM = (): string => "signOut";

    // The landing guard blocks navigation on the probe, so a half-open connection would otherwise
    // leave the site showing nothing at all. Matches the other guard timeouts. A probe that runs out
    // of time is Unknown, not evidence either way.
    public static PROBE_TIMEOUT_MS = (): number => 5000;
}

export class GuardConstants {
    public static PERMISSION_LOAD_TIMEOUT_MS = (): number => 5000;

    // How long featureFlagGuard waits for an organization's flags before giving up and letting the
    // navigation through. Falling through leaves the state Pending, which does not block.
    public static FEATURE_FLAG_LOAD_TIMEOUT_MS = (): number => 5000;

    // How long userLoadedGuard waits for the authorization state to arrive before treating the load
    // as failed. Named here rather than inline in the guard so it sits beside the retry budget below -
    // the two have to be reasoned about together, since the retries run inside this window.
    public static AUTHORIZATION_STATE_TIMEOUT_MS = (): number => 10000;

    // Transient-failure retry policy for the two calls userLoadedGuard depends on: GET users/{id} on
    // the Authentication API and GET authorization/{userId} on Core. Linear backoff, so 500/1000/1500ms
    // - at most ~3s added before the guard gives up and redirects to /system/auth-error.
    public static AUTH_LOAD_MAX_RETRIES = (): number => 3;
    public static AUTH_LOAD_RETRY_BASE_DELAY_MS = (): number => 500;

    // How long authCallbackErrorGuard waits for Auth0 to publish the error it redirected with. The SDK
    // navigates first and records the error straight after, so this only matters when something reaches
    // the route without a failed callback behind it.
    public static AUTH_CALLBACK_ERROR_TIMEOUT_MS = (): number => 2000;
}

export class AuthCallbackConstants {
    // Where auth0-angular sends a failed redirect callback (errorPath in app.config.ts). The SDK drops
    // the error query string on the way, so the route's guard reads the error from AuthService.error$.
    public static ERROR_PATH = (): string => "auth-callback-error";

    // What Auth0 redirects with when a blocked user signs in. The Authentication API blocks a user in
    // Auth0 when they are deactivated.
    public static BLOCKED_ERROR = (): string => "unauthorized";
    public static BLOCKED_ERROR_DESCRIPTION = (): string => "user is blocked";
}

export class MemberConstants {
    // From the `MEMBER_INVITATION_EXPIRATION_DAYS` GitHub var (substituted into
    // environment.config.json at deploy time); must match the API's own setting. Coerced because CI
    // can write it as a string, falling back to 10 days.
    public static INVITATION_EXPIRATION_DAYS = (): number => {
        const days = Number(environment.memberInvitationExpirationDays);
        return Number.isFinite(days) && days > 0 ? days : 10;
    };
}

export class PaymentConstants {
    public static INVOICE_SCHEDULE_AMOUNT_MINIMUM = (): number => 5.00;
    // 1 and 12 are left out because they duplicate Monthly and Annually.
    public static CUSTOM_FREQUENCY_MONTHS_OPTIONS = (): number[] => [2, 3, 4, 6, 24];
}

export class PaymentConstansts extends PaymentConstants {}
